<script setup>
  import { computed, ref, watch } from "vue";

  import api, { getUserInfo, setUserInfo } from "@/lib/api";
  import { TEXT } from "@/utils/constant";

  const props = defineProps({
    show: {
      type: Boolean,
      required: true,
    },
  });

  const emits = defineEmits(["update:show", "cancel"]);

  const localShow = computed({
    get: () => props.show,
    set: (val) => emits("update:show", val),
  });

  const closeModal = () => {
    emits("update:show", false);
  };

  const handleCancel = () => {
    emits("cancel");
  };

  const privacyContent = ref("");
  const policyId = ref(null);
  const checkedAgree = ref(false);
  const isLoading = ref(false);
  const isSubmitting = ref(false);
  // userInfo 선언 (Home.vue와 동일하게)
  const userInfo = ref(getUserInfo());

  const fetchPrivacyContent = async () => {
    if (isLoading.value) return;

    isLoading.value = true;
    try {
      const response = await api.post(
        `${process.env.VUE_APP_API_BASE_URL}/account/getAgreePolicyContent`,
      );
      console.log("체크");
      console.log(response);
      privacyContent.value = response.data?.result?.content || "";
      policyId.value = response.data?.result?.policyId || null;
    } catch (error) {
      console.error("Failed to fetch privacy content:", error);
      // Fallback to default content if API fails
      privacyContent.value = `<strong>「서울 학생 미래역량 진단 시험」 개인정보 수집·이용고지(학생용)</strong>
        <p>
          서울특별시교육청은 서울시 교수학습 서비스 일환으로 학생들의 검사 시행
          및 개인 결과표 제공을 위해 아래와 같이 응시학생의 개인정보를
          수집·이용함을 안내드립니다.
        </p>
        <strong>□ 개인정보 수집·이용에 대한 고지</strong>
        <p>
          개인정보보호법 제15조제1항 제3호,제4호에 따라 정보주체의 동의 없이
          개인정보를 수집·이용합니다.
        </p>
        <table>
          <colgroup>
            <col style="width: auto" />
            <col style="width: auto" />
            <col style="width: auto" />
          </colgroup>
          <thead>
            <tr>
              <th style="text-align: center">수집·이용 목적</th>
              <th style="text-align: center">수집 항목</th>
              <th style="text-align: center">수집 근거</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="text-align: center; white-space: normal;">
                서울 학생 미래역량 진단 서비스 제공, 사용자 인증 등 계정관리
              </td>
              <td style="text-align: center; word-break: keep-all; white-space: normal;">
                아이디, 이름, 생년월일, 성별 학교코드,<br/>학교명, 학년, 반, 번호<br/>(인공지능 맞춤형 교수학습 플랫폼에서<br/>제3자 제공으로 받은 정보)
              </td>
              <td style="text-align: center" rowspan="2">
                <strong>
                  초·중등교육법<br/>재30조의6,「개인정보보호법」제15조3항
                </strong>
              </td>
            </tr>
            <tr>
              <td style="text-align: center;">서울 학생 미래역량 진단 시험 실시 및 결과 분석, 과년도 검사 결과 비교 분석을 통한 성장 데이터 분석</td>
              <td style="text-align: center; white-space: normal;">
                검사결과 정보(시행년도, 회차, 검사구분, 검사명, 검사일정, 검사교시, 문항별 답안, 검사결과 수준(원점수, 변환점수, 평균점수, 영역수준, 기대성취수행)), 접속정보(접속IP, 접속일시, 시험 시행로그)
              </td>
            </tr>
          </tbody>
        </table>`;
    } finally {
      isLoading.value = false;
    }
  };

  const submitModal = async () => {
    if (isSubmitting.value) return;

    isSubmitting.value = true;
    try {
      await api.post(
        `${process.env.VUE_APP_API_BASE_URL}/account/agreePolicy`,
        {
          policyId: policyId.value,
        },
      );

      // 성공 후 사용자 정보 업데이트
      const currentUser = getUserInfo();
      setUserInfo({ ...currentUser, isAgreedValid: "Y" });

      emits("update:show", false);
    } catch (error) {
      console.error("Failed to submit privacy agreement:", error);
      alert("개인정보 동의 처리 중 오류가 발생했습니다. 다시 시도해 주세요.");
    } finally {
      isSubmitting.value = false;
    }
  };

  const updatePrivacy = async () => {
    try {
      const { data } = await api.post("/account/updatePrivacy", {});
      if (data?.status === 200) {
        // 모달 닫기 (Home.vue에서의 동작과 동일하게 emits 사용)
        emits("update:show", false);

        const currentUserData = JSON.parse(
          localStorage.getItem("user") || "{}",
        );
        currentUserData.isPrivacy = "Y";
        localStorage.setItem("user", JSON.stringify(currentUserData));

        // 그 다음 userInfo 업데이트
        userInfo.value = getUserInfo();
      }
    } catch (error) {
      console.error("Failed to update privacy agreement:", error);
      alert("개인정보 동의 처리 중 오류가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  // Watch for modal show changes to fetch content
  watch(
    () => props.show,
    (newShow) => {
      if (newShow && !privacyContent.value) {
        fetchPrivacyContent();
      }
    },
    { immediate: true },
  );
</script>

<template>
  <ui-modal
    v-model:show="localShow"
    title="개인정보 수집·이용 동의"
    :close-on-esc="false"
    :mask-closable="false"
    :closable="false"
  >
    <template #body>
      <div v-if="isLoading" class="p-4 text-center">로딩 중...</div>
      <div
        v-else
        class="board-detail-content whitespace-pre-line"
        v-html="privacyContent"
      ></div>
    </template>
    <template #footer>
      <div class="flex justify-end space-x-3">
        <ui-button color="primary" @click="updatePrivacy"> 확인 </ui-button>
      </div>
    </template>
  </ui-modal>
</template>
